import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ProductSearchResult {
  product_id: string;
  title: string;
  sku: string;
  vendor: string;
  category: string | null;
  lowest_price: number | null;
  total_quantity: number;
  in_stock: boolean;
  provider_id: string;
  provider_name: string;
}

export interface ProviderProductGroup {
  provider_id: string;
  provider_name: string;
  products: ProductSearchResult[];
}

export interface SearchFilters {
  searchTerm: string;
  category: string;
  vendor: string;
}

export const useProductSearch = (active: boolean) => {
  const [results, setResults] = useState<ProviderProductGroup[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allVendors, setAllVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState<SearchFilters>({
    searchTerm: '',
    category: '',
    vendor: '',
  });

  // Load available categories and vendors across all providers
  const loadFilterOptions = useCallback(async () => {
    try {
      const { data } = await supabase.rpc('get_all_product_filter_options', {});
      if (data && data.length > 0) {
        setAllCategories(data[0].categories || []);
        setAllVendors(data[0].vendors || []);
      }
    } catch (e: any) {
      // Fallback: load from product_descriptions directly
      try {
        const { data: catData } = await supabase
          .from('product_descriptions')
          .select('category')
          .not('category', 'is', null);
        const categories = Array.from(new Set((catData || []).map((r: any) => r.category).filter(Boolean)));
        setAllCategories(categories.sort());

        const { data: vendorData } = await supabase
          .from('product_descriptions')
          .select('vendor')
          .not('vendor', 'is', null);
        const vendors = Array.from(new Set((vendorData || []).map((r: any) => r.vendor).filter(Boolean)));
        setAllVendors(vendors.sort());
      } catch (_) {
        // Silent fallback
      }
    }
  }, []);

  // Search products across all providers
  const searchProducts = useCallback(async () => {
    if (!active) return;

    const hasSearch = filters.searchTerm.length >= 2;
    const hasFilters = !!filters.category || !!filters.vendor;

    if (!hasSearch && !hasFilters) {
      setResults([]);
      setTotalItems(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('search_all_products', {
        p_search: filters.searchTerm || null,
        p_category: filters.category || null,
        p_vendor: filters.vendor || null,
        p_limit: 100,
        p_offset: 0,
      });

      if (rpcError) throw rpcError;

      const rows: ProductSearchResult[] = (data || []).map((row: any) => ({
        product_id: row.product_id,
        title: row.title || 'Untitled',
        sku: row.sku || 'N/A',
        vendor: row.vendor || 'Unknown',
        category: row.category,
        lowest_price: row.lowest_price !== null ? Number(row.lowest_price) : null,
        total_quantity: Number(row.total_quantity),
        in_stock: row.in_stock,
        provider_id: row.provider_id,
        provider_name: row.provider_name,
      }));

      const total = rows.length > 0 ? Number((data as any[])[0].total_count) : 0;
      setTotalItems(total);

      // Group by provider
      const grouped = new Map<string, ProviderProductGroup>();
      for (const row of rows) {
        if (!grouped.has(row.provider_id)) {
          grouped.set(row.provider_id, {
            provider_id: row.provider_id,
            provider_name: row.provider_name,
            products: [],
          });
        }
        grouped.get(row.provider_id)!.products.push(row);
      }

      setResults(Array.from(grouped.values()));
    } catch (e: any) {
      console.error('Error searching products:', e);
      setError(e.message || 'Failed to search products');
    } finally {
      setLoading(false);
    }
  }, [active, filters]);

  useEffect(() => {
    if (active) {
      loadFilterOptions();
    }
  }, [active, loadFilterOptions]);

  useEffect(() => {
    searchProducts();
  }, [searchProducts]);

  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    setFilters({ searchTerm: '', category: '', vendor: '' });
    setResults([]);
    setTotalItems(0);
  };

  const hasActiveFilters = filters.searchTerm.length >= 2 || !!filters.category || !!filters.vendor;

  return {
    results,
    loading,
    error,
    filters,
    updateFilters,
    resetFilters,
    allCategories,
    allVendors,
    totalItems,
    hasActiveFilters,
  };
};
